import React, { useState, useRef, useCallback } from "react";
import axios from "axios";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import useFetch from "../../hooks/useFetch";
import { toast } from "react-toastify";

function UploadAction({ item, lockDaysBoolean, event, onSuccess }: any) {
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { fetchData: fetchCreateUpload } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/events/s3/create-upload`,
    method: "POST",
  });

  const { fetchData: fetchUpdateSongData } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/events/music/ready/${item.id}`,
    method: "PUT",
  });

  const handleUploadClick = () => fileInputRef.current?.click();

  const resetFileInput = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setProgress(0);
  }, []);

  const handleUploadComplete = useCallback(async () => {
    setProgress(0);
    try {
        const response = await fetchUpdateSongData();
        const updatedSong = response?.data;
        if (updatedSong?.status === "ready") {
          toast.info("Song uploaded and ready!");
          console.log(`Song is uploaded successful! songId ${response?.data?.id} ${response?.data?.status}.`);
        } else {
          resetFileInput();
          let reason;
          if (response?.success === false) {
            const errorDetails = response?.data?.details;
            reason = errorDetails?.reason + ": " + errorDetails?.details;
            console.log("Error reason:", reason);
          }
          
          toast.warn(
            <div>
              Some error occurred during music file upload, kindly upload again.<br />
              Reason: {reason}<br />
            </div>
          );
          console.log(`Error while music upload: ${response?.data?.details}.`);
        }
        if ( typeof onSuccess === "function") {
            onSuccess(updatedSong);
        }

    } catch (err) {
        console.error("Failed to update song data in DB:", err);
        alert("Upload was successful, but failed to update database.");
    }

  }, [fetchUpdateSongData, onSuccess]);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['audio/mpeg', 'audio/mp3', 'audio/mp4', 'audio/x-m4a', 'audio/m4a'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
    
    if (!allowedTypes.includes(file.type) && !['mp3', 'm4a'].includes(fileExtension)) {
      toast.warn("Please upload only MP3 or M4A files");
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const musicUploadSizeLimit = event.music_settings?.find(
      (setting: { key_name: string; value: number }) => setting.key_name === 'music_upload_size_limit_mb'
    )?.value;

    // console.log("event - music_upload_size_limit_mb from upload: ", musicUploadSizeLimit);
  
    
    const MAX_SIZE_MB = musicUploadSizeLimit;
    const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
  
    // console.log("MAX_SIZE_BYTES: ", MAX_SIZE_BYTES);

    if (file.size > MAX_SIZE_BYTES) {
      toast.warn(`File is too large! Maximum allowed size is ${MAX_SIZE_MB} MB.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }


    try {
        //create upload URL
        const result = await fetchCreateUpload({ song_id: item.id, item_song_id: item.song_id, class_name: item.class, event_id: item.event_id, fileName: file.name, fileType: file.type, fileSize: file.size });
        const uploadUrl = result?.data?.uploadUrl;

        if (!uploadUrl) throw new Error("No upload URL returned from server");

        //Upload file to aws s3
        await axios.put(uploadUrl, file, {
        headers: {
            "Content-Type": file.type,
        },
        onUploadProgress: (progressEvent) => {
            if (progressEvent.total && progressEvent.lengthComputable) {
                const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
                );
                setProgress(percentCompleted);
                /*if (percentCompleted === 100) {
                  setTimeout(() => handleUploadComplete(), 500); 
                }*/
            }
        },
        });
        await new Promise(r => setTimeout(r, 300));
        await handleUploadComplete();

        setProgress(0);
    } catch (err: unknown) {
      const error = err instanceof Error ? err.message : "Upload failed";
      alert(error);
      setProgress(0);
    }
  }, [fetchCreateUpload, item.id, handleUploadComplete]);

  return (
    <div className="upload-wrapper">
      <input
        type="file"
        accept=".mp3,.m4a,audio/*"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={lockDaysBoolean ? undefined : handleFileChange }
        disabled={lockDaysBoolean}
      />
      <div className={lockDaysBoolean ?  "disabled-action-container" :"" } style={{ position: "relative", display: "inline-block" }}>
        <FileUploadOutlinedIcon
          className={`music-icon upload ${lockDaysBoolean ?  "disabled-action" : "" }`}
          titleAccess={lockDaysBoolean ?  "" : "Upload"}
          onClick={lockDaysBoolean ? undefined : handleUploadClick }
          style={{
            cursor: lockDaysBoolean ?  "not-allowed" : "pointer",
            opacity: progress > 0 ? 0.5 : (lockDaysBoolean ?  0.5 : 1 ),
            color: lockDaysBoolean ? "#999" : "#000",
          }}
        />
        {lockDaysBoolean && (
          <span className="disabled-tooltip">
            Uploads are locked by Admin
          </span>
        )}
      </div>
      {progress > 0 && <span className="upload-progress">{progress}%</span>}
    </div>
  );
};

export default UploadAction;